import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Upload, Download, FileText, History, ArrowLeft, RefreshCw,
  UserSquare2, Banknote, Store, Trash2, Undo2, CalendarClock, AlertTriangle,
  CheckCircle2, MinusCircle, PlusCircle, FileWarning, FileSpreadsheet, Scale
} from 'lucide-react';
import api from '../../api';
import { encodeLan } from '../../api';

const PANEL_BG = '#f5f6f9';
const NAVY = '#0b2545';

function Section({ icon: Icon, title, children, right }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: 16, marginBottom: 16
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: NAVY, fontWeight: 700 }}>
          {Icon && <Icon size={16} />}
          <span>{title}</span>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function ImportCard({ title, description, sampleHref, importPath, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(importPath, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      if (onDone) onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 10 }}>
      <div style={{ fontWeight: 600, color: NAVY }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{description}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        <a href={sampleHref} className="btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Download size={14} /> Sample CSV
        </a>
        <input
          type="file" accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ fontSize: 12 }}
        />
        <button className="btn-navy" disabled={!file || busy} onClick={upload}>
          <Upload size={14} style={{ marginRight: 4 }} /> {busy ? 'Importing…' : 'Upload'}
        </button>
      </div>
      {err && <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>{err}</div>}
      {result && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <div style={{ color: '#15803d' }}>
            {result.imported !== undefined && <>Imported: <b>{result.imported}</b> · </>}
            {result.updated !== undefined && <>Updated: <b>{result.updated}</b> · </>}
            Skipped: <b>{result.skipped || 0}</b>
          </div>
          {Array.isArray(result.errors) && result.errors.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary>{result.errors.length} row error(s)</summary>
              <ul style={{ margin: '6px 0 0 18px', color: '#b91c1c' }}>
                {result.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function fmtIST(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
  } catch { return String(ts); }
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityKind, setEntityKind] = useState('');
  const [action, setAction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityKind) params.set('entityKind', entityKind);
      if (action) params.set('action', action);
      params.set('limit', '300');
      const res = await api.get(`/recovery/audit-log?${params.toString()}`);
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [entityKind, action]);

  useEffect(() => { load(); }, [load]);

  return (
    <Section
      icon={History}
      title="Audit Log"
      right={
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="btn-outline"
            onClick={async () => {
              try {
                const params = new URLSearchParams();
                if (entityKind) params.set('entityKind', entityKind);
                if (action) params.set('action', action);
                const res = await api.get(`/recovery/audit-log/export?${params.toString()}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `recovery_audit_log_${new Date().toISOString().slice(0,10)}.csv`);
                document.body.appendChild(link); link.click(); link.remove();
              } catch (e) {
                alert('Export failed');
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Download size={12} /> Export CSV
          </button>
          <button className="btn-outline" onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <select className="form-input" style={{ maxWidth: 180 }} value={entityKind} onChange={(e) => setEntityKind(e.target.value)}>
          <option value="">All entities</option>
          <option value="customer">Customer</option>
          <option value="loan">Loan</option>
          <option value="action">Recovery action</option>
          <option value="promise">PTP</option>
          <option value="payment">Payment</option>
        </select>
        <select className="form-input" style={{ maxWidth: 160 }} value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          <option value="edit">Edit</option>
          <option value="delete">Delete</option>
          <option value="restore">Restore</option>
          <option value="reset_pin">Reset PIN</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>No audit entries yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>When (IST)</th>
                <th style={{ padding: 8 }}>By</th>
                <th style={{ padding: 8 }}>Role</th>
                <th style={{ padding: 8 }}>Entity</th>
                <th style={{ padding: 8 }}>Action</th>
                <th style={{ padding: 8 }}>Loan #</th>
                <th style={{ padding: 8 }}>Changes</th>
                <th style={{ padding: 8 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r._id} style={{ borderTop: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                  <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{fmtIST(r.createdAt)}</td>
                  <td style={{ padding: 8 }}>{r.userName || r.userEmployeeId || '—'}</td>
                  <td style={{ padding: 8, textTransform: 'capitalize' }}>{r.userRole || '—'}</td>
                  <td style={{ padding: 8, textTransform: 'capitalize' }}>{r.entityKind}</td>
                  <td style={{ padding: 8, textTransform: 'capitalize' }}>{r.action}</td>
                  <td style={{ padding: 8 }}>{r.loanAccountNumber || '—'}</td>
                  <td style={{ padding: 8 }}>
                    {Array.isArray(r.changedFields) && r.changedFields.length > 0 ? (
                      <details>
                        <summary>{r.changedFields.length} field(s)</summary>
                        <ul style={{ margin: '4px 0 0 16px' }}>
                          {r.changedFields.map((f) => (
                            <li key={f}>
                              <b>{f}</b>: {String(r.before?.[f] ?? '∅')} → {String(r.after?.[f] ?? '∅')}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : r.action === 'delete' ? <span style={{ color: '#b91c1c' }}>deleted</span> : '—'}
                  </td>
                  <td style={{ padding: 8, color: '#6b7280' }}>{r.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function TrashTab() {
  const [data, setData] = useState({ actions: [], promises: [], payments: [], customers: [], loans: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/recovery/trash');
      setData({ actions: [], promises: [], payments: [], customers: [], loans: [], ...(res.data || {}) });
    } catch {
      setData({ actions: [], promises: [], payments: [], customers: [], loans: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (kind, id, restorePath) => {
    if (!window.confirm(`Restore this ${kind}?`)) return;
    setBusyId(id);
    try {
      await api.post(restorePath);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const daysLeft = (deletedAt) => {
    if (!deletedAt) return '';
    const ms = Date.now() - new Date(deletedAt).getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - days);
  };

  const renderTable = (kind, rows, label) => (
    <Section icon={Trash2} title={`${label} (${rows.length})`}>
      {rows.length === 0 ? (
        <div style={{ padding: 12, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>None.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>Deleted (IST)</th>
                <th style={{ padding: 8 }}>{kind === 'customer' ? 'Client ID' : kind === 'loan' ? 'Loan #' : 'Loan #'}</th>
                <th style={{ padding: 8 }}>Summary</th>
                <th style={{ padding: 8 }}>Reason</th>
                <th style={{ padding: 8 }}>Days left</th>
                <th style={{ padding: 8 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const id = r.id || r._id;
                let summary;
                let restorePath;
                let idCol = r.loanAccountNumber || '—';
                if (kind === 'customer') {
                  summary = `${r.fullName || ''} · ${r.primaryPhone || ''}`;
                  idCol = r.clientId || '—';
                  restorePath = `/recovery/customers/${encodeURIComponent(r.clientId)}/restore`;
                } else if (kind === 'loan') {
                  summary = `₹${r.totalOverdueAmount ?? '?'} overdue · ${r.productType || ''}`;
                  restorePath = `/recovery/loans/${encodeLan(r.loanAccountNumber)}/restore`;
                } else if (kind === 'payment') {
                  summary = `₹${r.amount ?? '?'} · ${r.method || ''}`;
                  restorePath = `/recovery/payments/${id}/restore`;
                } else if (kind === 'promise') {
                  summary = `₹${r.amount ?? '?'} on ${r.promiseDate || ''}`;
                  restorePath = `/recovery/promises/${id}/restore`;
                } else {
                  summary = `${r.actionType || ''}: ${(r.notes || '').slice(0, 60)}`;
                  restorePath = `/recovery/actions/${id}/restore`;
                }
                return (
                  <tr key={id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{fmtIST(r.deletedAt)}</td>
                    <td style={{ padding: 8 }}>{idCol}</td>
                    <td style={{ padding: 8 }}>{summary}</td>
                    <td style={{ padding: 8, color: '#6b7280' }}>{r.deletedReason || '—'}</td>
                    <td style={{ padding: 8 }}>{daysLeft(r.deletedAt)}</td>
                    <td style={{ padding: 8 }}>
                      <button
                        className="btn-outline"
                        disabled={busyId === id}
                        onClick={() => restore(kind, id, restorePath)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Undo2 size={12} /> Restore
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );

  if (loading) return <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Loading…</div>;

  return (
    <>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
        Items deleted within the last 30 days can be restored. After 30 days they are permanently removed.
      </div>
      {renderTable('customer', data.customers || [], 'Deleted customers')}
      {renderTable('loan', data.loans || [], 'Deleted loans')}
      {renderTable('payment', data.payments || [], 'Deleted payments')}
      {renderTable('promise', data.promises || [], 'Deleted PTPs')}
      {renderTable('action', data.actions || [], 'Deleted recovery actions')}
    </>
  );
}

function fmtINR(v) {
  if (v === null || v === undefined || v === '') return '—';
  try { return '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }); } catch { return String(v); }
}

function ReconciliationTab() {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/recovery/reconciliations', { params: { status } });
      setRows(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r) => {
    const reason = window.prompt('Resolution reason (required):');
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/recovery/reconciliations/${r.id}/resolve`, { reason: reason.trim() });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Resolve failed');
    }
  };

  return (
    <Section icon={Scale} title="Reconciliations" right={
      <div style={{ display: 'flex', gap: 8 }}>
        <select className="form-input" style={{ fontSize: 12, padding: '4px 8px' }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <button className="btn-outline" onClick={load}><RefreshCw size={12} /></button>
      </div>
    }>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        Loans where the CBS outstanding change didn't match the portal-recorded payments since the previous import (tolerance ₹100). ↓ = outstanding dropped (expected), ↑ = outstanding rose (interest/penalty accrual).
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#6b7280' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6b7280' }}>No {status === 'all' ? '' : status} reconciliations.</div>
      ) : (
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f9fafb', textAlign: 'left' }}>
            <th style={th}>Report</th><th style={th}>Loan A/C</th><th style={th}>Customer</th>
            <th style={thR}>Prev OS</th><th style={thR}>Curr OS</th><th style={thR}>O/S Change</th>
            <th style={thR}>Payments</th><th style={thR}>Unexplained</th>
            <th style={th}>Status</th><th style={th}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid #e5e7eb', background: r.status === 'open' ? '#fff7ed' : 'transparent' }}>
                <td style={td}>{r.reportDate}</td>
                <td style={td}>{r.loanAccountNumber}</td>
                <td style={td}>{r.customerName}</td>
                <td style={tdR}>{fmtINR(r.previousOutstanding)}</td>
                <td style={tdR}>{fmtINR(r.currentOutstanding)}</td>
                <td style={{ ...tdR, color: (r.actualDrop || 0) > 0.5 ? '#15803d' : ((r.actualDrop || 0) < -0.5 ? '#b91c1c' : undefined), fontWeight: 600 }} title={(r.actualDrop || 0) >= 0 ? 'Outstanding decreased (payments / write-offs)' : 'Outstanding increased (interest / penalty accrual)'}>
                  {(r.actualDrop || 0) > 0.5 ? '↓ ' : ((r.actualDrop || 0) < -0.5 ? '↑ ' : '')}{fmtINR(Math.abs(r.actualDrop || 0))}
                </td>
                <td style={tdR}>{fmtINR(r.portalPaymentsSince)}</td>
                <td style={{ ...tdR, color: '#b91c1c', fontWeight: 600 }}>{fmtINR(r.unexplainedDelta)}</td>
                <td style={td}>
                  {r.status === 'open' ? <span style={{ color: '#a16207' }}>open</span>
                                       : <span style={{ color: '#15803d' }}>resolved</span>}
                  {r.resolutionReason && <div style={{ fontSize: 10, color: '#6b7280' }}>{r.resolutionReason}</div>}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-outline" onClick={() => navigate(`/owner/recovery/cases/${encodeLan(r.loanAccountNumber)}`)}>Open case</button>
                    {r.status === 'open' && <button className="btn-navy" onClick={() => resolve(r)}>Resolve</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function LoanStatementsTab() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const upload = async () => {
    if (!files.length) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      const res = await api.post('/admin/statements/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setFiles([]);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Section icon={FileSpreadsheet} title="Loan account statements (bulk)">
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
          Upload one or many <code>CEO_rptLoanStatement*.pdf</code> files. Each statement is matched to its loan account by
          the A/C No. printed on page 1. We extract customer name, mobile, address, guarantors, scheme, EMI, open date, due date,
          and clear the “statement needed” flag.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="file" accept=".pdf,application/pdf" multiple
            onChange={(e) => { setFiles(Array.from(e.target.files || [])); setResult(null); setErr(''); }}
            style={{ fontSize: 12 }}
          />
          <button className="btn-navy" disabled={!files.length || busy} onClick={upload}>
            <Upload size={14} style={{ marginRight: 4 }} /> {busy ? 'Uploading…' : `Upload ${files.length || ''} file(s)`}
          </button>
        </div>
        {err && <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>{err}</div>}
      </Section>

      {result && (
        <Section icon={CheckCircle2} title={`Result: ${result.summary.ok} ok · ${result.summary.errors} error(s)`}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f9fafb', textAlign: 'left' }}>
              <th style={th}>File</th><th style={th}>Loan A/C</th><th style={th}>Customer</th><th style={th}>Guarantors</th><th style={th}>Status</th>
            </tr></thead>
            <tbody>
              {result.results.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid #e5e7eb', background: r.status === 'error' ? '#fef2f2' : 'transparent' }}>
                  <td style={td}>{r.fileName}</td>
                  <td style={td}>{r.loanAccountNumber || '—'}</td>
                  <td style={td}>{r.customerName || '—'}</td>
                  <td style={td}>{r.guarantors ?? '—'}</td>
                  <td style={td}>
                    {r.status === 'ok' ? (
                      <span style={{ color: '#15803d' }}>updated{r.customerUpdated ? ' (+customer)' : ''}</span>
                    ) : (
                      <span style={{ color: '#b91c1c' }}>{r.error}</span>
                    )}
                    {Array.isArray(r.warnings) && r.warnings.length > 0 && (
                      <div style={{ color: '#a16207', fontSize: 11 }}>⚠ {r.warnings.join('; ')}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </>
  );
}

function WeeklyOverdueTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);
  const [applyResult, setApplyResult] = useState(null);
  const [expand, setExpand] = useState({});
  // vanishedResolutions: { [loanAccountNumber]: { resolution, amountPaid, amountWaived, notes } }
  const [vanishedResolutions, setVanishedResolutions] = useState({});
  // Old-account reconciliation
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [reconcileErr, setReconcileErr] = useState('');

  const runReconcile = async (dryRun) => {
    setReconcileBusy(true); setReconcileErr(''); setReconcileResult(null);
    try {
      const res = await api.post('/admin/recovery/reconcile-orphaned', { dryRun });
      setReconcileResult({ ...res.data, dryRun });
    } catch (e) {
      setReconcileErr(e.response?.data?.detail || 'Reconciliation failed');
    } finally { setReconcileBusy(false); }
  };

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/admin/overdue/imports');
      setHistory(res.data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr(''); setPreview(null); setApplyResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/admin/overdue/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const cancelPreview = async () => {
    if (!preview?.importId) { setPreview(null); return; }
    try { await api.delete(`/admin/overdue/imports/${preview.importId}`); } catch { /* ignore */ }
    setPreview(null); setFile(null); setErr(''); setVanishedResolutions({}); loadHistory();
  };

  const applyPreview = async () => {
    if (!preview?.importId) return;

    // Only HITL accounts require explicit resolutions; auto-close accounts are handled server-side
    const vanishedHitl = preview.vanishedHitl || [];
    const unresolved = vanishedHitl.filter(v => !vanishedResolutions[v.loanAccountNumber]?.resolution);
    if (unresolved.length > 0) {
      setErr(`Please select a resolution for all ${unresolved.length} flagged account(s) before applying.`);
      setExpand(p => ({ ...p, van: true }));
      return;
    }

    if (!window.confirm(`Apply this import? ${preview.summary.willCreate} new loans will be created and ${preview.summary.willUpdate} existing loans will be updated.`)) return;
    setBusy(true); setErr('');
    try {
      const resolutions = vanishedHitl.map(v => ({
        loanAccountNumber: v.loanAccountNumber,
        ...vanishedResolutions[v.loanAccountNumber],
        amountPaid: parseFloat(vanishedResolutions[v.loanAccountNumber]?.amountPaid || 0),
        amountWaived: parseFloat(vanishedResolutions[v.loanAccountNumber]?.amountWaived || 0),
      }));
      const res = await api.post(`/admin/overdue/apply/${preview.importId}`, { vanishedResolutions: resolutions });
      setApplyResult(res.data);
      setPreview(null); setFile(null); setVanishedResolutions({});
      loadHistory();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  const toggle = (k) => setExpand((p) => ({ ...p, [k]: !p[k] }));

  return (
    <>
      <Section icon={CalendarClock} title="Weekly Overdue Import (CBS PDF)">
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
          Upload the CBS-generated <code>CEO_LoanOverDue.pdf</code>. We parse every row, compare against the last applied import,
          and show you exactly what will change before anything is written. Stubs are created for unknown loans (you can upload their loan statements later).
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".pdf,application/pdf" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setApplyResult(null); setErr(''); }} style={{ fontSize: 12 }} />
          <button className="btn-navy" disabled={!file || busy} onClick={runPreview}>
            <Upload size={14} style={{ marginRight: 4 }} /> {busy ? 'Parsing…' : 'Preview'}
          </button>
        </div>
        {err && <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>{err}</div>}
        {applyResult && (
          <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 10, fontSize: 12, color: '#166534' }}>
            <b>Applied</b> · created {applyResult.counts.created} · updated {applyResult.counts.updated} · no-change {applyResult.counts.noChange} · skipped {applyResult.counts.skipped}
            {applyResult.errors?.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary>{applyResult.errors.length} error(s)</summary>
                <ul style={{ margin: '6px 0 0 18px' }}>{applyResult.errors.slice(0, 50).map((e, i) => <li key={i}>{e}</li>)}</ul>
              </details>
            )}
          </div>
        )}
      </Section>

      {preview && (
        <Section icon={FileText} title={`Preview · Report date: ${preview.reportDate || 'unknown'}`}
                 right={
                   <div style={{ display: 'flex', gap: 8 }}>
                     <button className="btn-outline" onClick={cancelPreview} disabled={busy}>Cancel</button>
                     <button className="btn-navy" onClick={applyPreview} disabled={busy}>{busy ? 'Applying…' : 'Apply changes'}</button>
                   </div>
                 }>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
            <SummaryPill label="Total rows" value={preview.summary.totalRows} color="#0b2545" />
            <SummaryPill label="Will create" value={preview.summary.willCreate} color="#15803d" icon={PlusCircle} />
            <SummaryPill label="Will update" value={preview.summary.willUpdate} color="#1d4ed8" icon={RefreshCw} />
            <SummaryPill label="No change" value={preview.summary.noChange} color="#6b7280" icon={CheckCircle2} />
            <SummaryPill label="Auto-closed" value={preview.summary.vanishedAuto ?? 0} color="#15803d" icon={CheckCircle2} />
            <SummaryPill label="Needs review" value={preview.summary.vanishedHitl ?? preview.summary.vanished} color="#a16207" icon={MinusCircle} />
            <SummaryPill label="Discrepancies" value={preview.summary.discrepancies} color="#b91c1c" icon={AlertTriangle} />
            <SummaryPill label="Warnings" value={preview.summary.warnings} color="#a16207" icon={FileWarning} />
          </div>
          {preview.previousReportDate && (
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
              Comparing against previous import dated <b>{preview.previousReportDate}</b>
            </div>
          )}

          {preview.warnings?.length > 0 && (
            <DetailsBlock open={expand.warnings} onToggle={() => toggle('warnings')} title={`Warnings (${preview.warnings.length})`}>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#a16207' }}>
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </DetailsBlock>
          )}

          {preview.discrepancies?.length > 0 && (
            <DetailsBlock open={expand.disc} onToggle={() => toggle('disc')}
                          title={`Discrepancies (${preview.discrepancies.length})`} accent="#b91c1c">
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#fef2f2', textAlign: 'left' }}>
                  <th style={th}>Loan A/C</th><th style={th}>Name</th><th style={thR}>Prev OS</th><th style={thR}>Curr OS</th><th style={thR}>O/S Change</th><th style={thR}>Payments</th><th style={thR}>Unexplained</th>
                </tr></thead>
                <tbody>
                  {preview.discrepancies.map((d, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #fecaca' }}>
                      <td style={td}>{d.loanAccountNumber}</td>
                      <td style={td}>{d.customerName}</td>
                      <td style={tdR}>{fmtINR(d.previousOutstanding)}</td>
                      <td style={tdR}>{fmtINR(d.currentOutstanding)}</td>
                      <td style={{ ...tdR, color: (d.actualDrop || 0) > 0.5 ? '#15803d' : ((d.actualDrop || 0) < -0.5 ? '#b91c1c' : undefined), fontWeight: 600 }} title={(d.actualDrop || 0) >= 0 ? 'Outstanding decreased' : 'Outstanding increased'}>
                        {(d.actualDrop || 0) > 0.5 ? '↓ ' : ((d.actualDrop || 0) < -0.5 ? '↑ ' : '')}{fmtINR(Math.abs(d.actualDrop || 0))}
                      </td>
                      <td style={tdR}>{fmtINR(d.portalPaymentsSince)}</td>
                      <td style={{ ...tdR, color: '#b91c1c', fontWeight: 600 }}>{fmtINR(d.unexplainedDelta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DetailsBlock>
          )}

          {preview.willCreate?.length > 0 && (
            <DetailsBlock open={expand.create} onToggle={() => toggle('create')}
                          title={`Will create (${preview.willCreate.length})`} accent="#15803d">
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f0fdf4', textAlign: 'left' }}>
                  <th style={th}>Loan A/C</th><th style={th}>Customer</th><th style={th}>Mobile</th><th style={th}>GL</th><th style={thR}>Loan amt</th><th style={thR}>Outstanding</th><th style={thR}>Overdue</th>
                </tr></thead>
                <tbody>
                  {preview.willCreate.map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #bbf7d0' }}>
                      <td style={td}>{r.loanAccountNumber}</td>
                      <td style={td}>{r.customerName} {r.mappingMissing && <span title="Branch or GL mapping missing" style={{ color: '#b91c1c' }}>⚠</span>}</td>
                      <td style={td}>{r.mobile}</td>
                      <td style={td}>{r.glCode}</td>
                      <td style={tdR}>{fmtINR(r.loanAmount)}</td>
                      <td style={tdR}>{fmtINR(r.outstandingAmount)}</td>
                      <td style={tdR}>{fmtINR(r.overdueTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DetailsBlock>
          )}

          {preview.willUpdate?.length > 0 && (
            <DetailsBlock open={expand.update} onToggle={() => toggle('update')}
                          title={`Will update (${preview.willUpdate.length})`} accent="#1d4ed8">
              {preview.willUpdate.map((r, i) => (
                <div key={i} style={{ borderTop: i > 0 ? '1px solid #e5e7eb' : 'none', padding: '8px 0' }}>
                  <div style={{ fontWeight: 600, color: NAVY, fontSize: 12 }}>
                    {r.loanAccountNumber} · <span style={{ fontWeight: 400 }}>{r.customerName}</span>
                  </div>
                  <table style={{ width: '100%', fontSize: 11, marginTop: 4 }}>
                    <tbody>
                      {Object.entries(r.changes).map(([field, { old, new: nv }]) => (
                        <tr key={field}>
                          <td style={{ ...td, color: '#6b7280', width: 200 }}>{field}</td>
                          <td style={tdR}>{typeof old === 'number' ? fmtINR(old) : (old ?? '—')}</td>
                          <td style={{ padding: '2px 6px', color: '#6b7280' }}>→</td>
                          <td style={{ ...tdR, color: '#1d4ed8', fontWeight: 600 }}>{typeof nv === 'number' ? fmtINR(nv) : (nv ?? '—')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </DetailsBlock>
          )}

          {/* Auto-close group — silent, no action needed */}
          {(preview.vanishedAuto?.length || 0) > 0 && (
            <DetailsBlock open={expand.vanAuto} onToggle={() => toggle('vanAuto')}
                          title={`Auto-closed on apply (${preview.vanishedAuto.length}) — portal payments cover full overdue`} accent="#15803d">
              <div style={{ fontSize: 12, color: '#166534', marginBottom: 8 }}>
                These accounts will be silently closed when you apply the import. No admin input required.
              </div>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f0fdf4', textAlign: 'left' }}>
                  <th style={th}>Loan A/C</th><th style={th}>Customer</th><th style={thR}>Prev Overdue</th><th style={thR}>Portal Paid</th>
                </tr></thead>
                <tbody>
                  {preview.vanishedAuto.map((v, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #bbf7d0' }}>
                      <td style={td}>{v.loanAccountNumber}</td>
                      <td style={td}>{v.customerName}</td>
                      <td style={tdR}>{fmtINR(v.previousOverdueTotal)}</td>
                      <td style={{ ...tdR, color: '#15803d', fontWeight: 600 }}>{fmtINR(v.portalPaymentsSince)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DetailsBlock>
          )}

          {/* HITL group — admin must pick reason before apply is allowed */}
          {(preview.vanishedHitl?.length || 0) > 0 && (
            <DetailsBlock open={expand.van} onToggle={() => toggle('van')}
                          title={`Needs review before applying (${preview.vanishedHitl.length}) — closure reason unclear`} accent="#a16207">
              <div style={{ fontSize: 12, color: '#92400e', marginBottom: 10 }}>
                These accounts vanished from the report but portal payments don't fully explain why. Select a resolution for each before applying.
              </div>
              {preview.vanishedHitl.map((v) => {
                const res = vanishedResolutions[v.loanAccountNumber] || {};
                const setRes = (patch) => setVanishedResolutions(p => ({ ...p, [v.loanAccountNumber]: { ...res, ...patch } }));
                const isPaidType = res.resolution === 'paid_closed' || res.resolution === 'waived_closed';
                return (
                  <div key={v.loanAccountNumber} style={{ border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: res.resolution ? '#fefce8' : '#fffbeb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>{v.loanAccountNumber}</span>
                      <span style={{ fontSize: 12, color: '#374151' }}>{v.customerName}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                        Prev overdue: <strong>{fmtINR(v.previousOverdueTotal)}</strong>
                        {' · '}Portal payments: <strong style={{ color: v.portalPaymentsSince > 0 ? '#15803d' : '#b91c1c' }}>{fmtINR(v.portalPaymentsSince)}</strong>
                        {v.suggestedWaivedOff > 0 && (
                          <>{' · '}Gap: <strong style={{ color: '#b45309' }}>{fmtINR(v.suggestedWaivedOff)}</strong></>
                        )}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      <select
                        value={res.resolution || ''}
                        onChange={e => setRes({ resolution: e.target.value })}
                        style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: `1px solid ${res.resolution ? '#d1d5db' : '#f59e0b'}`, background: res.resolution ? '#fff' : '#fef9c3', minWidth: 200 }}
                      >
                        <option value="">— Select resolution —</option>
                        <option value="paid_closed">✅ Fully Paid &amp; Closed</option>
                        <option value="waived_closed">💰 Paid + Interest Waived Off</option>
                        <option value="other">📝 Other Reason (add note)</option>
                        <option value="no_action">⏩ Skip — still active, follow up later</option>
                      </select>
                      {isPaidType && (
                        <input type="number" placeholder="Amount paid (₹)" min="0" step="0.01"
                               value={res.amountPaid || ''}
                               onChange={e => setRes({ amountPaid: e.target.value })}
                               style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', width: 150 }} />
                      )}
                      {res.resolution === 'waived_closed' && (
                        <input type="number" placeholder="Amount waived (₹)" min="0" step="0.01"
                               value={res.amountWaived || ''}
                               onChange={e => setRes({ amountWaived: e.target.value })}
                               style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', width: 150 }} />
                      )}
                      <input type="text" placeholder="Notes"
                             value={res.notes || ''}
                             onChange={e => setRes({ notes: e.target.value })}
                             style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', flex: 1, minWidth: 160 }} />
                    </div>
                  </div>
                );
              })}
            </DetailsBlock>
          )}

          {preview.noChange?.length > 0 && (
            <DetailsBlock open={expand.nc} onToggle={() => toggle('nc')}
                          title={`No change (${preview.noChange.length})`} accent="#6b7280">
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {preview.noChange.slice(0, 50).map((n) => n.loanAccountNumber).join(', ')}
                {preview.noChange.length > 50 && ` … +${preview.noChange.length - 50} more`}
              </div>
            </DetailsBlock>
          )}
        </Section>
      )}

      <Section icon={History} title="Import history" right={<button className="btn-outline" onClick={loadHistory}><RefreshCw size={12} /></button>}>
        {history.length === 0 ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>No imports yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f9fafb', textAlign: 'left' }}>
              <th style={th}>Report date</th><th style={th}>Rows</th><th style={th}>Status</th><th style={th}>By</th><th style={th}>Uploaded</th><th style={th}>Applied</th><th style={th}>Actions</th>
            </tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={td}>{h.reportDate || '—'}</td>
                  <td style={td}>{h.rowCount}</td>
                  <td style={td}>
                    {h.applied ? <span style={{ color: '#15803d' }}>applied</span> : <span style={{ color: '#a16207' }}>draft</span>}
                  </td>
                  <td style={td}>{h.createdByName || ''}</td>
                  <td style={td}>{fmtIST(h.createdAt)}</td>
                  <td style={td}>{fmtIST(h.appliedAt)}</td>
                  <td style={td}>
                    {!h.applied && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn-navy"
                          disabled={busy}
                          onClick={async () => {
                            if (!window.confirm(`Apply draft import from ${h.reportDate || h.createdAt}?`)) return;
                            setBusy(true); setErr('');
                            try {
                              const res = await api.post(`/admin/overdue/apply/${h.id}`);
                              setApplyResult(res.data);
                              loadHistory();
                            } catch (e) {
                              setErr(e.response?.data?.detail || 'Apply failed');
                            } finally { setBusy(false); }
                          }}
                          style={{ padding: '4px 8px', fontSize: 11 }}
                        >Apply</button>
                        <button
                          className="btn-outline"
                          disabled={busy}
                          onClick={async () => {
                            if (!window.confirm('Discard this draft import? It cannot be recovered.')) return;
                            setBusy(true); setErr('');
                            try {
                              await api.delete(`/admin/overdue/imports/${h.id}`);
                              loadHistory();
                            } catch (e) {
                              setErr(e.response?.data?.detail || 'Discard failed');
                            } finally { setBusy(false); }
                          }}
                          style={{ padding: '4px 8px', fontSize: 11, color: '#b91c1c' }}
                        >Discard</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* ---- Retrospective Old-Account Reconciliation ---- */}
      <Section icon={RefreshCw} title="Reconcile Old Accounts (one-time)">
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
          Scans all active CBS-origin loan accounts against the <strong>latest applied overdue import</strong>.
          Accounts absent from that report are either auto-closed (if portal payments cover the full overdue)
          or flagged for admin review (if the closure reason is unclear).
          Run <strong>Preview</strong> first to see what will change, then <strong>Apply</strong> to commit.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <button className="btn-outline" disabled={reconcileBusy} onClick={() => runReconcile(true)}>
            {reconcileBusy ? 'Running…' : '🔍 Preview (dry run)'}
          </button>
          <button className="btn-navy" disabled={reconcileBusy} onClick={async () => {
            if (!window.confirm('Apply reconciliation? Auto-closeable accounts will be silently closed and ambiguous ones will be flagged for review.')) return;
            runReconcile(false);
          }}>
            {reconcileBusy ? 'Applying…' : '⚡ Apply reconciliation'}
          </button>
        </div>
        {reconcileErr && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{reconcileErr}</div>}
        {reconcileResult && (
          <div style={{ fontSize: 12 }}>
            <div style={{ background: reconcileResult.dryRun ? '#fef9c3' : '#f0fdf4', border: `1px solid ${reconcileResult.dryRun ? '#fde68a' : '#bbf7d0'}`, borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <strong>{reconcileResult.dryRun ? '🔍 Dry run — no writes made' : '✅ Applied'}</strong>
              {' · '}Latest report: <b>{reconcileResult.latestReportDate || 'N/A'}</b>
              {' · '}Orphaned: <b>{reconcileResult.totalOrphaned}</b>
              {' · '}Auto-closed: <b style={{ color: '#15803d' }}>{reconcileResult.autoClosed}</b>
              {' · '}Flagged for review: <b style={{ color: '#a16207' }}>{reconcileResult.flagged}</b>
            </div>
            {reconcileResult.flaggedDetails?.length > 0 && (
              <details style={{ marginBottom: 8 }}>
                <summary style={{ cursor: 'pointer', color: '#a16207', fontWeight: 600 }}>
                  Flagged accounts ({reconcileResult.flaggedDetails.length}) — open reconciliations created
                </summary>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                  <thead><tr style={{ background: '#fef3c7', textAlign: 'left' }}>
                    <th style={th}>Loan A/C</th><th style={th}>Customer</th><th style={thR}>Prev Overdue</th><th style={thR}>Portal Paid</th><th style={thR}>Gap</th>
                  </tr></thead>
                  <tbody>
                    {reconcileResult.flaggedDetails.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #fde68a' }}>
                        <td style={td}>{r.loanAccountNumber}</td>
                        <td style={td}>{r.customerName}</td>
                        <td style={tdR}>{fmtINR(r.previousOverdue)}</td>
                        <td style={tdR}>{fmtINR(r.portalPayments)}</td>
                        <td style={{ ...tdR, color: '#b91c1c', fontWeight: 600 }}>{fmtINR(r.unexplainedAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
            {reconcileResult.autoClosedDetails?.length > 0 && (
              <details>
                <summary style={{ cursor: 'pointer', color: '#15803d', fontWeight: 600 }}>
                  Auto-closed accounts ({reconcileResult.autoClosedDetails.length})
                </summary>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                  <thead><tr style={{ background: '#f0fdf4', textAlign: 'left' }}>
                    <th style={th}>Loan A/C</th><th style={th}>Customer</th><th style={thR}>Prev Overdue</th><th style={thR}>Portal Paid</th>
                  </tr></thead>
                  <tbody>
                    {reconcileResult.autoClosedDetails.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #bbf7d0' }}>
                        <td style={td}>{r.loanAccountNumber}</td>
                        <td style={td}>{r.customerName}</td>
                        <td style={tdR}>{fmtINR(r.previousOverdue)}</td>
                        <td style={{ ...tdR, color: '#15803d', fontWeight: 600 }}>{fmtINR(r.portalPayments)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}
      </Section>
    </>
  );
}

const th = { padding: '6px 8px', fontWeight: 600, color: '#374151' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '4px 8px', verticalAlign: 'top' };
const tdR = { ...td, textAlign: 'right' };

function SummaryPill({ label, value, color, icon: Icon }) {
  return (
    <div style={{ border: `1px solid ${color}33`, background: `${color}10`, borderRadius: 8, padding: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color, textTransform: 'uppercase', fontWeight: 600 }}>
        {Icon && <Icon size={11} />}{label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function DetailsBlock({ open, onToggle, title, accent = '#0b2545', children }) {
  return (
    <div style={{ border: `1px solid ${accent}33`, borderRadius: 8, marginTop: 8 }}>
      <button onClick={onToggle} style={{ width: '100%', textAlign: 'left', padding: '8px 10px', background: `${accent}08`, border: 'none', cursor: 'pointer', fontWeight: 600, color: accent, fontSize: 12 }}>
        {open ? '▼' : '▶'} {title}
      </button>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const [tab, setTab] = useState('overdue');

  return (
    <div style={{ minHeight: '100vh', background: PANEL_BG }}>
      {/* Top Bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-outline"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            title="Back to field mode"
          >
            <ArrowLeft size={14} /> Field
          </button>
          <Shield size={20} color={NAVY} />
          <div>
            <div style={{ fontWeight: 700, color: NAVY }}>Admin Panel</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{user.name} · {user.employeeId}</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '16px auto', padding: '0 16px' }}>
        {/* Tabs */}
        <div className="period-tabs" style={{ marginBottom: 14 }}>
          <button className={`period-tab ${tab === 'overdue' ? 'active' : ''}`} onClick={() => setTab('overdue')}>
            <CalendarClock size={12} style={{ marginRight: 4 }} /> Weekly Overdue
          </button>
          <button className={`period-tab ${tab === 'statements' ? 'active' : ''}`} onClick={() => setTab('statements')}>
            <FileSpreadsheet size={12} style={{ marginRight: 4 }} /> Loan Statements
          </button>
          <button className={`period-tab ${tab === 'recon' ? 'active' : ''}`} onClick={() => setTab('recon')}>
            <Scale size={12} style={{ marginRight: 4 }} /> Reconciliation
          </button>
          <button className={`period-tab ${tab === 'imports' ? 'active' : ''}`} onClick={() => setTab('imports')}>
            <Upload size={12} style={{ marginRight: 4 }} /> Imports
          </button>
          <button className={`period-tab ${tab === 'corrections' ? 'active' : ''}`} onClick={() => setTab('corrections')}>
            <FileText size={12} style={{ marginRight: 4 }} /> Data Corrections
          </button>
          <button className={`period-tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>
            <History size={12} style={{ marginRight: 4 }} /> Audit Log
          </button>
          <button className={`period-tab ${tab === 'trash' ? 'active' : ''}`} onClick={() => setTab('trash')}>
            <Trash2 size={12} style={{ marginRight: 4 }} /> Trash
          </button>
        </div>

        {tab === 'imports' && (
          <>
            <Section icon={Banknote} title="Loan overdue / outstanding (bulk)">
              <ImportCard
                title="Update existing loans"
                description="Bulk-update Total Overdue, Outstanding, Penalty, Last Paid Date. Loan account number must already exist. Every row is audit-logged."
                sampleHref={`${api.defaults.baseURL}/admin/loans/import/sample`}
                importPath={'/admin/loans/import'}
              />
            </Section>
            <Section icon={Store} title="Merchants (acquisition)">
              <ImportCard
                title="Import merchants"
                description="Add new merchants in bulk with services, agent attribution, and optional first-visit note."
                sampleHref={`${api.defaults.baseURL}/merchants/import/sample`}
                importPath={'/merchants/import'}
              />
            </Section>
          </>
        )}

        {tab === 'corrections' && (
          <>
            <Section icon={UserSquare2} title="Customer corrections">
              <p style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                Open the Customers list to edit name, phone, alternate phone, or address.
                Every save is recorded in the audit log with before/after.
              </p>
              <button className="btn-navy" onClick={() => navigate('/owner/recovery/customers')}>
                Open Customers
              </button>
            </Section>
            <Section icon={Banknote} title="Loan account corrections">
              <p style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                Edit EMI, outstanding principal, due day, repayment frequency, assignment, etc.
                Every save is recorded in the audit log with before/after.
              </p>
              <button className="btn-navy" onClick={() => navigate('/owner/recovery/loans')}>
                Open Loan Accounts
              </button>
            </Section>
          </>
        )}

        {tab === 'overdue' && <WeeklyOverdueTab />}
        {tab === 'statements' && <LoanStatementsTab />}
        {tab === 'recon' && <ReconciliationTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'trash' && <TrashTab />}
      </div>
    </div>
  );
}
